import { Checkbox, Form, InputNumber, Select, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { CustomInput } from "sipli-fleet-core/components";
import { SearchIcon } from "src/assets/svgs";
import { useQueryGetUserDevices, useQueryGetUserTimedData } from "src/queries/users.queries";
import type { UserDevice, UserTimedDataPermission } from "./types";
import { getResponseArray } from "./userModalHelpers";

interface LimitationsState {
  // Persist hydration across step unmounts without replacing unsaved selections.
  hydratedFor?: string;
  historicalEnabled: boolean;
  selectedPeriod?: string | number;
  monthsAgo?: number;
  selectedVehicles: Array<string | number>;
}

interface LimitationsStepProps {
  userId?: string | number;
  limitations: LimitationsState;
  setLimitations: (state: LimitationsState) => void;
  showVehicleLimitations: boolean;
}

const MAX_MONTHS = 120;
const SearchIconComponent = SearchIcon as unknown as ComponentType<{ className?: string }>;

const LimitationsStep = ({
  userId,
  limitations,
  setLimitations,
  showVehicleLimitations,
}: LimitationsStepProps) => {
  const { t } = useTranslation();
  const [vehicleSearch, setVehicleSearch] = useState("");
  const devicesQuery = useQueryGetUserDevices(userId, {
    enabled: Boolean(userId && showVehicleLimitations),
  });
  const timedDataQuery = useQueryGetUserTimedData(userId, {
    enabled: Boolean(userId),
  });

  const devices = useMemo(
    () => getResponseArray<UserDevice>(devicesQuery.data),
    [devicesQuery.data]
  );
  const filteredDevices = useMemo(() => {
    const search = vehicleSearch.trim().toLowerCase();
    return devices.filter((device) =>
      [device.name, device.licensePlate, device.imei, device.id].some((value) =>
        String(value ?? "").toLowerCase().includes(search)
      )
    );
  }, [devices, vehicleSearch]);
  const timedOptions = useMemo(
    () => getResponseArray<UserTimedDataPermission>(timedDataQuery.data),
    [timedDataQuery.data]
  );
  const selectedTimedOption = timedOptions.find(
    (option) => String(option.settingId) === String(limitations.selectedPeriod)
  );

  useEffect(() => {
    const nextHydratedKey = userId
      ? `${userId}:${showVehicleLimitations ? "vehicles" : "no-vehicles"}`
      : undefined;

    if (!userId || !nextHydratedKey || limitations.hydratedFor === nextHydratedKey) return;
    if (timedDataQuery.isLoading || (showVehicleLimitations && devicesQuery.isLoading)) return;

    const checkedVehicleIds = showVehicleLimitations
      ? devices.filter((device) => device.isChecked).map((device) => device.id)
      : [];
    const selectedTimedData = timedOptions.find((option) => option.isSelected);

    setLimitations({
      hydratedFor: nextHydratedKey,
      historicalEnabled: Boolean(selectedTimedData),
      selectedPeriod: selectedTimedData?.settingId,
      monthsAgo: selectedTimedData?.code === "CUSTOM_DATE" ? selectedTimedData.monthAgo : undefined,
      selectedVehicles: checkedVehicleIds,
    });
  }, [
    devices,
    devicesQuery.isLoading,
    limitations.hydratedFor,
    setLimitations,
    showVehicleLimitations,
    timedDataQuery.isLoading,
    timedOptions,
    userId,
  ]);

  const allSelected =
    devices.length > 0 &&
    devices.every((device) =>
      limitations.selectedVehicles.some((vehicleId) => String(vehicleId) === String(device.id))
    );
  const someSelected = devices.some((device) =>
    limitations.selectedVehicles.some((vehicleId) => String(vehicleId) === String(device.id))
  );
  const updateLimitations = (next: Partial<LimitationsState>) => {
    setLimitations({ ...limitations, ...next });
  };
  const toggleVehicle = (vehicleId: string | number, checked?: boolean) => {
    const isSelected = limitations.selectedVehicles.some((id) => String(id) === String(vehicleId));
    const shouldSelect = checked ?? !isSelected;

    updateLimitations({
      selectedVehicles: shouldSelect
        ? [...limitations.selectedVehicles.filter((id) => String(id) !== String(vehicleId)), vehicleId]
        : limitations.selectedVehicles.filter((id) => String(id) !== String(vehicleId)),
    });
  };

  const columns: ColumnsType<UserDevice> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          disabled={!devices.length}
          onChange={(event) => {
            updateLimitations({
              selectedVehicles: event.target.checked ? devices.map((device) => device.id) : [],
            });
          }}
        />
      ),
      dataIndex: "selection",
      width: 52,
      render: (_value, record) => (
        <Checkbox
          checked={limitations.selectedVehicles.some((id) => String(id) === String(record.id))}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            toggleVehicle(record.id, event.target.checked);
          }}
        />
      ),
    },
    {
      title: t("license_plate"),
      dataIndex: "licensePlate",
      key: "licensePlate",
      sorter: (first, second) =>
        String(first.licensePlate ?? "").localeCompare(String(second.licensePlate ?? "")),
      render: (value) => <strong>{value || "--"}</strong>,
    },
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: (first, second) => String(first.name ?? "").localeCompare(String(second.name ?? "")),
      render: (value) => <strong>{value || "--"}</strong>,
    },
    {
      title: t("imei"),
      dataIndex: "imei",
      key: "imei",
      sorter: (first, second) => String(first.imei ?? "").localeCompare(String(second.imei ?? "")),
      render: (value) => <strong>{value || "--"}</strong>,
    },
  ];

  const now = dayjs();
  const fromDate =
    limitations.monthsAgo && limitations.monthsAgo > 0
      ? now.subtract(limitations.monthsAgo, "month")
      : null;

  return (
    <div className="user-modal-step">
      <h3 className="user-modal-section-title">{t("limitations")}</h3>

      <section className="user-limit-section">
        <h4>{t("historical_period")}</h4>
        <div className="user-historical-card">
          <Checkbox
            checked={limitations.historicalEnabled}
            onChange={(event) => {
              const checked = event.target.checked;
              updateLimitations({
                historicalEnabled: checked,
                selectedPeriod: checked ? limitations.selectedPeriod : undefined,
                monthsAgo: checked ? limitations.monthsAgo : undefined,
              });
            }}
          >
            <strong>{t("enable_historical_limit")}</strong>
          </Checkbox>

          <div className="user-historical-select">
            <span>{t("limit_period")}:</span>
            <Select
              disabled={!limitations.historicalEnabled}
              loading={timedDataQuery.isLoading}
              value={limitations.selectedPeriod}
              placeholder={t("limit_period")}
              options={timedOptions.map((option) => ({
                label: t(option.code ?? option.name ?? String(option.settingId)),
                value: option.settingId,
              }))}
              onChange={(value) =>
                updateLimitations({
                  selectedPeriod: value,
                  monthsAgo:
                    timedOptions.find((option) => String(option.settingId) === String(value))
                      ?.code === "CUSTOM_DATE"
                      ? limitations.monthsAgo
                      : undefined,
                })
              }
            />
          </div>
        </div>

        {limitations.historicalEnabled && selectedTimedOption?.code === "CUSTOM_DATE" ? (
          <Form.Item className="user-months-field">
            <InputNumber
              min={1}
              max={MAX_MONTHS}
              value={limitations.monthsAgo}
              placeholder={t("months_ago_placeholder")}
              onChange={(value) =>
                updateLimitations({
                  monthsAgo: typeof value === "number" && value > 0 ? value : undefined,
                })
              }
            />
            <span>
              {limitations.monthsAgo
                ? t("showing_range", {
                    from: fromDate?.format("MMM DD, YYYY") ?? "",
                    to: now.format("MMM DD, YYYY"),
                  })
                : t("enter_number_of_months")}
            </span>
          </Form.Item>
        ) : null}
      </section>

      {showVehicleLimitations ? (
        <section className="user-limit-section">
          <div className="user-limit-heading">
            <h4>{t("manageable_vehicle")}</h4>
            <div className="user-vehicle-tools">
              <CustomInput
                className="user-vehicle-search"
                aria-label={t("search_vehicle")}
                placeholder={t("search_vehicle")}
                prefix={<SearchIconComponent className="h-[14px] w-[14px]" />}
                allowClear
                value={vehicleSearch}
                onChange={(event) => setVehicleSearch(event.target.value)}
              />
              <button
                type="button"
                className="user-select-all-tag"
                onClick={() => {
                  updateLimitations({
                    selectedVehicles: allSelected ? [] : devices.map((device) => device.id),
                  });
                }}
              >
                {allSelected ? t("deselect_all") : t("select_all")}
              </button>
            </div>
          </div>
          <Table<UserDevice>
            className="user-vehicle-table"
            loading={devicesQuery.isLoading}
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={filteredDevices}
            pagination={false}
            scroll={{ x: 600, y: 380 }}
            locale={{ emptyText: t(vehicleSearch.trim() ? "no_vehicle_found" : "no_data") }}
            rowClassName={(record) =>
              limitations.selectedVehicles.some((id) => String(id) === String(record.id))
                ? "user-vehicle-row-selected"
                : ""
            }
            onRow={(record) => ({
              onClick: () => toggleVehicle(record.id),
            })}
          />
        </section>
      ) : (
        <div className="user-no-limitations">{t("no_limitations_for_the_role")}</div>
      )}
    </div>
  );
};

export type { LimitationsState };
export default LimitationsStep;
